import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm, useCan, useSelectedDealer, useSelectedLocation } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import {
  useDeleteChecklistMutation,
  useDetachChecklistFromDealerMutation,
  useDetachChecklistFromLocationMutation,
  useDuplicateChecklistMutation,
  useExtendChecklistMutation
} from "modules/ChecklistsDashboard/components/EditChecklistModal/components/EditChecklistActions/hooks";
import { CHECKLIST_LISTS } from "modules/ChecklistsDashboard/util";

import type { Checklist, ChecklistID } from "models";

type EditChecklistActionsProps = {
  checklist: Checklist;
  currentList: CHECKLIST_LISTS;
  canEdit: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onOpenChecklist: (checklistID: ChecklistID) => void;
};

export const EditChecklistActions = ({ checklist, currentList, canEdit, isSubmitting, onClose, onOpenChecklist }: EditChecklistActionsProps) => {
  const { t } = useTranslation();

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isCopyConfirmOpen, setIsCopyConfirmOpen] = useState(false);

  const { selectedLocation } = useSelectedLocation();
  const { selectedDealer } = useSelectedDealer();
  const { mutateAsync: extendChecklist, isPending: isExtendPending } = useExtendChecklistMutation();
  const { mutateAsync: duplicateChecklist, isPending: isDuplicatePending } = useDuplicateChecklistMutation();
  const { mutateAsync: deleteChecklist, isPending: isDeletePending } = useDeleteChecklistMutation();
  const { mutateAsync: detachChecklistFromLocation, isPending: isDetachFromLocationPending } = useDetachChecklistFromLocationMutation();
  const { mutateAsync: detachChecklistFromDealer, isPending: isDetachFromDealerPending } = useDetachChecklistFromDealerMutation();

  const canExtend = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.Checklists);
  const canDetachFromLocation = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.LocationChecklistAttachment);
  const canDetachFromDealer = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.DealerChecklistAttachment);
  const canDelete = useCan(ACL_ACTIVITIES.Delete, checklist.dealer_id ? ACL_RESOURCES.Checklists : ACL_RESOURCES.DefaultChecklists);

  const isLocationList = currentList === CHECKLIST_LISTS.Location;
  const isDealerDetach = currentList === CHECKLIST_LISTS.Dealer && !checklist.dealer_id;
  const isDetach = isLocationList || isDealerDetach;
  const isRemovePending = isDeletePending || isDetachFromLocationPending || isDetachFromDealerPending;
  const canRemove = (isLocationList && canDetachFromLocation) || (isDealerDetach && canDetachFromDealer) || (!isDetach && canDelete);

  const handleExtend = async () => {
    const extendedChecklistID = await extendChecklist({
      parent_id: checklist.id,
      name: `${checklist.name} extended`,
      dealer_location_id: isLocationList ? selectedLocation?.id : undefined,
      dealer_id: isLocationList ? undefined : selectedDealer?.id
    });

    onOpenChecklist(extendedChecklistID);
  };

  const handleCopy = async () => {
    if (!selectedDealer) {
      setIsCopyConfirmOpen(false);
      return;
    }

    await duplicateChecklist({ checklist_id: checklist.id, dealer_id: selectedDealer.id });
    setIsCopyConfirmOpen(false);
    toast.success(t("v8_default_checklist_successfully_copied", "The default checklist was copied successfully"));
  };

  const handleDelete = async () => {
    if (isLocationList && selectedLocation) await detachChecklistFromLocation({ checklist_id: checklist.id, dealer_location_id: selectedLocation.id });
    else if (isDealerDetach && selectedDealer) await detachChecklistFromDealer({ checklist_id: checklist.id, dealer_id: selectedDealer.id });
    else if (!isDetach) await deleteChecklist({ id: checklist.id });
    else {
      setIsDeleteConfirmOpen(false);
      return;
    }

    setIsDeleteConfirmOpen(false);
    onClose();
  };

  return (
    <>
      <Modal.Actions>
        <div className="actions-left">
          {canRemove && (
            <Button type="button" color="red" onClick={() => setIsDeleteConfirmOpen(true)}>
              {isDetach ? t("v8_detach", "Detach") : t("v8_delete", "Delete")}
              <Icon className={isDetach ? "link slash" : "trash"} />
            </Button>
          )}

          {canExtend && !checklist.dealer_id && currentList !== CHECKLIST_LISTS.Default && (
            <Button type="button" color="orange" disabled={isExtendPending} loading={isExtendPending} onClick={handleExtend}>
              {t("v8_extend", "Extend")}
            </Button>
          )}

          {!checklist.dealer_id && (
            <Button type="button" color="orange" onClick={() => setIsCopyConfirmOpen(true)}>
              {t("v8_copy", "Copy")}
            </Button>
          )}
        </div>

        <div className="actions-right">
          <Button type="button" className="light" onClick={onClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>

          {canEdit && (
            <Button type="submit" color="green" disabled={isSubmitting} loading={isSubmitting}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Button>
          )}
        </div>
      </Modal.Actions>

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={isDeleteConfirmOpen}
        isLoading={isRemovePending}
        customTitle={t("v8_are_you_sure", "Are you sure?")}
        confirmMsg={
          isDetach
            ? t("v8_detach_checklist_confirm_message", "Are you sure that you want to detach this checklist?")
            : t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")
        }
        handleConfirm={handleDelete}
        handleCancel={() => setIsDeleteConfirmOpen(false)}
      />

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Warning}
        isOpen={isCopyConfirmOpen}
        isLoading={isDuplicatePending}
        confirmMsg={t("v8_confirm_copy_checklist", "Do you want to create a copy of this checklist?")}
        handleConfirm={handleCopy}
        handleCancel={() => setIsCopyConfirmOpen(false)}
      />
    </>
  );
};
